import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthImage from "@/components/AuthImage";
import { toast } from "sonner";
import {
  CarProfile, CheckCircle, Warning, Wrench, ListChecks, CircleNotch,
  TreeStructure, ArrowLeft, PaperPlaneTilt, Sparkle, Printer, Camera, PencilSimple, FloppyDisk,
  YoutubeLogo, BookOpen, Info,
} from "@phosphor-icons/react";

export default function DiagnosticView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState("");
  const [notes, setNotes] = useState("");
  const [advancing, setAdvancing] = useState(false);
  const [selectedStepIdx, setSelectedStepIdx] = useState(null);
  const [credits, setCredits] = useState(null);
  const [paywall, setPaywall] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/diagnostic/${id}`);
      setDiag(data);
      setSelectedStepIdx(data.steps.length - 1);
      // Pull fresh credit state so we can preview the paywall if the next
      // question will consume a credit.
      try {
        const r = await api.get("/credits");
        setCredits(r.data);
      } catch (err) {
        console.warn("Credits fetch failed on diag view:", err);
      }
    } catch {
      toast.error("Session not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Paywall predicate: at step 2, if the diag hasn't already been paid for
  // AND the shop has no paid credit left on any track, the next Advance
  // will 402. Show inline paywall instead of letting the LLM burn.
  const paidCreditsRemaining =
    credits?.plan_unlimited
      ? Infinity
      : (credits?.monthly_remaining ?? 0) + (credits?.balance ?? 0);
  const willHitPaywall =
    diag?.steps?.length >= 2 &&
    !diag?.credit_charged &&
    !diag?.done &&
    credits !== null &&
    paidCreditsRemaining === 0;

  const submitAnswer = async () => {
    if (!answer.trim()) {
      toast.error("Choose or type an answer");
      return;
    }
    setAdvancing(true);
    try {
      const currentStep = diag.steps[diag.steps.length - 1];
      const { data } = await api.post(`/diagnostic/${id}/answer`, {
        step_id: currentStep.id,
        answer,
        notes,
      });
      setDiag(data);
      setSelectedStepIdx(data.steps.length - 1);
      setAnswer("");
      setNotes("");
      // Refresh credits — we may have just consumed one.
      try {
        const r = await api.get("/credits");
        setCredits(r.data);
      } catch (err) {
        console.warn("Credits refresh after advance failed:", err);
      }
      if (data.done) toast.success("Diagnosis complete");
    } catch (e) {
      if (e?.response?.status === 402) {
        setPaywall(true);
      } else {
        toast.error(e?.response?.data?.detail || "Failed to advance");
      }
    } finally {
      setAdvancing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto border border-primary rounded-md scan-line" />
          <div className="mt-6 font-display text-2xl">Loading session…</div>
        </div>
      </Layout>
    );
  }

  if (!diag) return null;

  const currentIdx = diag.steps.length - 1;
  const shownIdx = selectedStepIdx ?? currentIdx;
  const shownStep = diag.steps[shownIdx];
  const isCurrent = shownIdx === currentIdx && !diag.done;

  const answeredMap = {};
  diag.history.forEach((h) => { answeredMap[h.step_id] = h; });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <button onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-white text-sm inline-flex items-center gap-1 mb-4" data-testid="back-to-dash">
          <ArrowLeft weight="bold" className="w-4 h-4" /> Back to dashboard
        </button>

        {/* Header */}
        <div className="border border-border rounded-md p-5 bg-card mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-primary/20 border border-primary/40 flex items-center justify-center">
                <CarProfile weight="bold" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="font-display text-3xl font-bold tracking-tight">
                  {diag.vehicle.year} {diag.vehicle.make} {diag.vehicle.model}
                </div>
                <div className="text-xs font-mono-tech text-muted-foreground flex flex-wrap gap-x-3 mt-0.5">
                  {diag.vehicle.engine && <span>{diag.vehicle.engine}</span>}
                  {diag.vehicle.vin && <span>VIN {diag.vehicle.vin}</span>}
                </div>
              </div>
            </div>
            {diag.done ? (
              <span className="px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 font-mono-tech uppercase text-xs flex items-center gap-1.5">
                <CheckCircle weight="bold" className="w-4 h-4" /> Solved
              </span>
            ) : (
              <span className="px-3 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 font-mono-tech uppercase text-xs flex items-center gap-1.5">
                <CircleNotch weight="bold" className="w-4 h-4" /> In progress
              </span>
            )}
          </div>
          <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] font-mono-tech text-muted-foreground uppercase tracking-widest">Complaint</div>
              <div className="text-white/90">{diag.complaint}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono-tech text-muted-foreground uppercase tracking-widest">Observations / DTC</div>
              <div className="font-mono-tech text-white/90">{diag.observations || "—"}</div>
            </div>
          </div>
          {diag.summary && (
            <div className="mt-4 border-t border-border pt-3 text-sm italic text-muted-foreground">
              <Sparkle weight="fill" className="w-4 h-4 text-primary inline mr-1" />
              {diag.summary}
            </div>
          )}
          {diag.photo_paths && diag.photo_paths.length > 0 && (
            <div className="mt-4 border-t border-border pt-3">
              <div className="text-[10px] font-mono-tech text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Camera weight="bold" className="w-3.5 h-3.5" /> Attached photos
              </div>
              <div className="flex flex-wrap gap-2">
                {diag.photo_paths.map((p) => (
                  <a key={p} href="#" onClick={(e) => e.preventDefault()} className="block w-20 h-20 border border-border rounded-md overflow-hidden">
                    <AuthImage path={p} className="w-full h-full object-cover" alt="case photo" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left: causes + flowchart */}
          <div className="lg:col-span-4 space-y-6">
            <CausesPanel causes={diag.possible_causes} />
            <FlowPanel
              steps={diag.steps}
              answeredMap={answeredMap}
              selectedIdx={shownIdx}
              currentIdx={currentIdx}
              onSelect={setSelectedStepIdx}
              done={diag.done}
            />
          </div>

          {/* Right: current step / final */}
          <div className="lg:col-span-8">
            {diag.done ? (
              <FinalDiagnosis diag={diag} onUpdate={setDiag} />
            ) : (paywall || willHitPaywall) ? (
              <PaywallCard
                onBuy={() => navigate("/billing")}
                dismissable={willHitPaywall && !paywall}
                onDismiss={() => setPaywall(false)}
                remaining={paidCreditsRemaining}
              />
            ) : (
              <StepPanel
                step={shownStep}
                answered={answeredMap[shownStep?.id]}
                isCurrent={isCurrent}
                answer={answer}
                setAnswer={setAnswer}
                notes={notes}
                setNotes={setNotes}
                onSubmit={submitAnswer}
                advancing={advancing}
                nextChargesCredit={diag?.steps?.length === 2 && !diag.credit_charged}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function CausesPanel({ causes }) {
  return (
    <div className="border border-border rounded-md bg-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks weight="bold" className="w-4 h-4 text-primary" />
        <div className="font-display text-lg tracking-tight uppercase">Possible Causes</div>
      </div>
      <div className="space-y-2">
        {(causes || []).map((c, i) => (
          <div key={`${i}-${c.cause}`} className="border border-border rounded-md p-3 hover:border-primary/40 transition-colors">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium">{c.cause}</div>
              <LikelihoodBadge level={c.likelihood} />
            </div>
            <div className="text-xs text-muted-foreground mt-1">{c.reasoning}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LikelihoodBadge({ level }) {
  const map = {
    high: "text-red-400 border-red-500/40 bg-red-500/10",
    medium: "text-yellow-400 border-yellow-500/40 bg-yellow-500/10",
    low: "text-muted-foreground border-border bg-muted",
    ruled_out: "text-muted-foreground/60 border-border bg-transparent line-through",
  };
  const cls = map[level] || map.low;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase font-mono-tech ${cls}`}>{(level || "").replace("_", " ")}</span>;
}

function FlowPanel({ steps, answeredMap, selectedIdx, currentIdx, onSelect, done }) {
  return (
    <div className="border border-border rounded-md bg-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <TreeStructure weight="bold" className="w-4 h-4 text-primary" />
        <div className="font-display text-lg tracking-tight uppercase">Diagnostic Flow</div>
      </div>
      <ol className="relative border-l border-border ml-3 space-y-3">
        {steps.map((s, i) => {
          const answered = answeredMap[s.id];
          const isSelected = i === selectedIdx;
          const isCurrent = i === currentIdx && !done;
          return (
            <li key={s.id || i} className="pl-4 relative">
              <button
                onClick={() => onSelect(i)}
                className={`w-full text-left border rounded-md p-3 transition-colors ${
                  isSelected ? "border-primary bg-primary/5" : "border-border bg-background hover:border-primary/40"
                }`}
                data-testid={`flow-step-${i}`}
              >
                <span
                  className={`absolute -left-[9px] top-4 w-4 h-4 rounded-full border-2 ${
                    isCurrent ? "bg-primary border-primary shadow-[0_0_0_4px_hsl(262_83%_58%/0.2)]"
                    : answered ? "bg-green-500 border-green-500"
                    : "bg-background border-border"
                  }`}
                />
                <div className="font-mono-tech text-[10px] text-muted-foreground">STEP {i + 1}</div>
                <div className="font-display font-semibold leading-tight mt-0.5">{s.title}</div>
                {answered && (
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-1">→ {answered.answer}</div>
                )}
              </button>
            </li>
          );
        })}
        {done && (
          <li className="pl-4 relative">
            <span className="absolute -left-[9px] top-3 w-4 h-4 rounded-full bg-green-500 border-2 border-green-500" />
            <div className="border border-green-500/40 bg-green-500/10 rounded-md p-3">
              <div className="font-mono-tech text-[10px] text-green-400">FINAL</div>
              <div className="font-display font-semibold">Root cause confirmed</div>
            </div>
          </li>
        )}
      </ol>
    </div>
  );
}

function StepPanel({ step, answered, isCurrent, answer, setAnswer, notes, setNotes, onSubmit, advancing, nextChargesCredit }) {
  if (!step) return null;
  return (
    <div className="border border-border rounded-md bg-card p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono-tech text-xs text-primary uppercase tracking-widest">{step.id?.replace("_", " ")}</div>
          <h2 className="font-display font-bold text-3xl tracking-tight mt-1">{step.title}</h2>
          {step.goal && <div className="text-sm text-muted-foreground mt-2">Goal: {step.goal}</div>}
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-5">
        <InfoBlock icon={<ListChecks weight="bold" className="w-4 h-4" />} title="Test Procedure">
          <ol className="list-decimal ml-5 space-y-1 text-sm text-white/90">
            {(step.test_procedure || []).map((p, i) => <li key={i}>{p}</li>)}
          </ol>
        </InfoBlock>
        <InfoBlock icon={<Wrench weight="bold" className="w-4 h-4" />} title="Tools Required">
          <ul className="space-y-1 text-sm text-white/90">
            {(step.tools_required || []).map((t, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-primary" /> {t}
              </li>
            ))}
          </ul>
          {(step.preferred_tools || []).length > 0 && step.substitution_note && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[10px] font-mono-tech text-primary uppercase tracking-widest mb-1">Best-results tool{step.preferred_tools.length === 1 ? "" : "s"}</div>
              <div className="text-xs text-white/80">{step.preferred_tools.join(" · ")}</div>
              <div className="text-xs text-yellow-200/80 mt-1 italic">{step.substitution_note}</div>
            </div>
          )}
        </InfoBlock>
        <InfoBlock icon={<CheckCircle weight="bold" className="w-4 h-4" />} title="Expected Values" accent="primary">
          <div className="font-mono-tech text-sm text-white/90">{step.expected_values || "—"}</div>
        </InfoBlock>
        <InfoBlock icon={<Warning weight="bold" className="w-4 h-4" />} title="Safety Warnings" accent="warning">
          <ul className="space-y-1 text-sm text-yellow-100/90">
            {(step.safety_warnings || []).map((w, i) => <li key={i}>• {w}</li>)}
          </ul>
        </InfoBlock>
      </div>

      {/* Helpful references */}
      {(step.youtube_search || step.diagram_search) && (
        <div className="mt-6 border border-border rounded-md p-4 bg-background" data-testid="step-references">
          <div className="flex items-center gap-2 mb-2 text-muted-foreground">
            <Info weight="bold" className="w-4 h-4" />
            <div className="font-mono-tech text-xs uppercase tracking-widest">Helpful references</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {step.youtube_search && (
              <a
                href={`https://www.youtube.com/results?search_query=${encodeURIComponent(step.youtube_search)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-mono-tech px-3 py-2 rounded-md border border-border bg-secondary/40 hover:border-red-500/60 hover:text-red-300 transition-colors"
                data-testid="step-youtube-link"
              >
                <YoutubeLogo weight="fill" className="w-4 h-4 text-red-500" />
                <span className="text-white/90">Watch:</span>
                <span className="text-muted-foreground">{step.youtube_search}</span>
              </a>
            )}
            {step.diagram_search && (
              <a
                href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(step.diagram_search)}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-mono-tech px-3 py-2 rounded-md border border-border bg-secondary/40 hover:border-primary/60 hover:text-primary transition-colors"
                data-testid="step-diagram-link"
              >
                <BookOpen weight="bold" className="w-4 h-4 text-primary" />
                <span className="text-white/90">Diagrams:</span>
                <span className="text-muted-foreground">{step.diagram_search}</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Q&A */}
      <div className="mt-6 border-t border-border pt-6">
        <div className="font-mono-tech text-xs text-muted-foreground uppercase tracking-widest mb-1">// question</div>
        <div className="font-display text-xl">{step.question_to_ask}</div>

        {isCurrent && nextChargesCredit && (
          <div className="mt-3 border border-primary/40 bg-primary/5 rounded-md px-3 py-2 flex items-center gap-2" data-testid="credit-hint">
            <Info weight="fill" className="w-4 h-4 text-primary shrink-0" />
            <div className="text-xs text-primary/90 font-mono-tech">
              Heads up: answering this question uses <span className="text-primary font-semibold">1 credit</span> to unlock the next step. Short cases that resolve here stay free.
            </div>
          </div>
        )}

        {answered ? (
          <div className="mt-4 border border-green-500/40 bg-green-500/5 rounded-md p-4">
            <div className="text-xs font-mono-tech text-green-400 uppercase tracking-widest">Your answer</div>
            <div className="text-white/90 mt-1">{answered.answer}</div>
            {answered.notes && <div className="text-sm text-muted-foreground mt-2 italic">Notes: {answered.notes}</div>}
            {answered.reasoning && (
              <div className="mt-3 pt-3 border-t border-green-500/20 text-sm text-muted-foreground">
                <Sparkle weight="fill" className="w-3.5 h-3.5 text-primary inline mr-1" />
                {answered.reasoning}
              </div>
            )}
          </div>
        ) : isCurrent ? (
          <div className="mt-4 space-y-3">
            {(step.options || []).map((opt, i) => (
              <button
                key={`${i}-${opt.label}`}
                onClick={() => setAnswer(opt.label)}
                className={`w-full text-left border rounded-md p-3 transition-colors ${
                  answer === opt.label ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"
                }`}
                data-testid={`option-${i}`}
              >
                <div className="text-sm font-medium">{opt.label}</div>
                {opt.hint && <div className="text-xs text-muted-foreground mt-0.5">{opt.hint}</div>}
              </button>
            ))}
            <div>
              <Label className="text-xs font-mono-tech text-muted-foreground uppercase tracking-wider">Notes / observations (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Actual reading: 43 psi. Injectors clicked evenly..."
                className="mt-1 min-h-24 bg-input"
                data-testid="notes-input"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Or type your own answer..."
                className="flex-1 min-h-12 px-3 rounded-md bg-input border border-border text-white text-sm"
                data-testid="custom-answer-input"
              />
              <Button
                onClick={onSubmit}
                disabled={advancing || !answer.trim()}
                className="min-h-12 bg-primary hover:bg-primary/90"
                data-testid="submit-answer-btn"
              >
                {advancing ? "Thinking..." : <>Advance <PaperPlaneTilt weight="bold" className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>
            {advancing && (
              <div className="border border-border rounded-md p-4 bg-background scan-line">
                <div className="font-mono-tech text-xs text-primary">// re-ranking causes · picking next test</div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground mt-3">No answer recorded for this step.</div>
        )}
      </div>
    </div>
  );
}

function InfoBlock({ icon, title, children, accent }) {
  const borderCls = accent === "warning" ? "border-yellow-500/40" : accent === "primary" ? "border-primary/40" : "border-border";
  const iconCls = accent === "warning" ? "text-yellow-400" : accent === "primary" ? "text-primary" : "text-muted-foreground";
  return (
    <div className={`border ${borderCls} rounded-md p-4 bg-background`}>
      <div className={`flex items-center gap-2 mb-2 ${iconCls}`}>
        {icon}
        <div className="font-mono-tech text-xs uppercase tracking-widest">{title}</div>
      </div>
      {children}
    </div>
  );
}

function FinalDiagnosis({ diag, onUpdate }) {
  const navigate = useNavigate();
  const f = diag.final_diagnosis || {};
  const [editingLabor, setEditingLabor] = useState(false);
  const [laborDraft, setLaborDraft] = useState(f.estimated_labor_hours || "");
  const [savingLabor, setSavingLabor] = useState(false);

  const saveLabor = async () => {
    setSavingLabor(true);
    try {
      const { data } = await api.patch(`/diagnostic/${diag.diag_id}/final`, {
        estimated_labor_hours: laborDraft,
      });
      onUpdate({ ...diag, final_diagnosis: data });
      setEditingLabor(false);
      toast.success("Labor time updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSavingLabor(false); }
  };

  return (
    <div className="border border-green-500/40 bg-green-500/5 rounded-md p-6" data-testid="final-diagnosis">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-green-400">
          <CheckCircle weight="bold" className="w-5 h-5" />
          <div className="font-mono-tech text-xs uppercase tracking-widest">Confirmed Diagnosis</div>
        </div>
        <Button
          onClick={() => navigate(`/diagnostic/${diag.diag_id}/report`)}
          className="bg-primary hover:bg-primary/90 min-h-10"
          data-testid="open-report-btn"
        >
          <Printer weight="bold" className="w-4 h-4 mr-2" /> Print customer report
        </Button>
      </div>
      <h2 className="font-display font-bold text-4xl mt-3 tracking-tight">{f.root_cause}</h2>
      {f.confidence && (
        <div className="mt-2 text-sm text-muted-foreground">
          Confidence: <span className="text-white/90 uppercase font-mono-tech">{f.confidence}</span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <InfoBlock icon={<Wrench weight="bold" className="w-4 h-4" />} title="Repair Recommendation" accent="primary">
          <div className="text-sm text-white/90">{f.repair_recommendation}</div>
        </InfoBlock>
        <div className="border border-primary/40 rounded-md p-4 bg-background">
          <div className="flex items-center justify-between text-primary mb-2">
            <div className="flex items-center gap-2">
              <CircleNotch weight="bold" className="w-4 h-4" />
              <div className="font-mono-tech text-xs uppercase tracking-widest">Estimated Labor</div>
            </div>
            {!editingLabor && (
              <button
                onClick={() => { setLaborDraft(f.estimated_labor_hours || ""); setEditingLabor(true); }}
                className="text-muted-foreground hover:text-white transition-colors"
                data-testid="edit-labor-btn"
                title="Edit labor time"
              >
                <PencilSimple weight="bold" className="w-4 h-4" />
              </button>
            )}
          </div>
          {editingLabor ? (
            <div className="flex items-center gap-2">
              <Input
                value={laborDraft}
                onChange={(e) => setLaborDraft(e.target.value)}
                placeholder="1.0 - 1.5"
                className="font-mono-tech bg-input min-h-10"
                data-testid="labor-input"
              />
              <Button size="sm" onClick={saveLabor} disabled={savingLabor} className="bg-primary hover:bg-primary/90" data-testid="save-labor-btn">
                <FloppyDisk weight="bold" className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditingLabor(false)} data-testid="cancel-labor-btn">
                Cancel
              </Button>
            </div>
          ) : (
            <div className="font-mono-tech text-lg font-semibold text-white/90" data-testid="labor-display">
              {f.estimated_labor_hours || "—"} <span className="text-xs text-muted-foreground ml-1">hrs</span>
            </div>
          )}
        </div>
      </div>

      {f.customer_explanation && (
        <div className="mt-4 border border-border rounded-md p-4 bg-background">
          <div className="text-[10px] font-mono-tech text-muted-foreground uppercase tracking-widest mb-1.5">// customer-friendly explanation</div>
          <div className="text-sm text-white/90 leading-relaxed">{f.customer_explanation}</div>
        </div>
      )}

      {Array.isArray(f.parts) && f.parts.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Wrench weight="bold" className="w-4 h-4 text-primary" />
            <div className="font-mono-tech text-xs uppercase tracking-widest text-primary">Parts Required</div>
          </div>
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground">Part</th>
                  <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground">OEM #</th>
                  <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground">Aftermarket</th>
                  <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground text-right">Qty</th>
                  <th className="px-3 py-2 font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground text-right">Est. $</th>
                </tr>
              </thead>
              <tbody>
                {f.parts.map((p, i) => (
                  <tr key={i} className="border-t border-border align-top" data-testid={`part-row-${i}`}>
                    <td className="px-3 py-2 text-white/90">{p.name}</td>
                    <td className="px-3 py-2 font-mono-tech text-white/90">{p.oem_part_number || "—"}</td>
                    <td className="px-3 py-2 text-white/80 text-xs">
                      {(p.aftermarket_options || []).join(" · ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-tech text-white/90">{p.quantity ?? 1}</td>
                    <td className="px-3 py-2 text-right font-mono-tech text-white/90">${p.estimated_cost_usd || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {f.notes && (
        <div className="mt-4 border-t border-border pt-4 text-sm text-muted-foreground italic">
          <Sparkle weight="fill" className="w-4 h-4 text-primary inline mr-1" />
          {f.notes}
        </div>
      )}
    </div>
  );
}

function PaywallCard({ onBuy, dismissable, onDismiss, remaining }) {
  return (
    <div className="border border-primary/50 rounded-md bg-gradient-to-br from-primary/10 to-background p-8 text-center" data-testid="paywall-card">
      <div className="w-14 h-14 mx-auto rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center mb-4">
        <Sparkle weight="fill" className="w-7 h-7 text-primary" />
      </div>
      <div className="font-mono-tech text-xs text-primary tracking-widest uppercase mb-2">// unlock the full diagnostic</div>
      <h2 className="font-display text-3xl font-bold tracking-tight">You've used your 2 free questions on this case</h2>
      <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto leading-relaxed">
        Buy a credit to keep going — the AI will walk you through the rest of the flow chart,
        test procedures, and final diagnosis including parts & labor estimate for this case.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
        <Button
          onClick={onBuy}
          className="min-h-12 bg-primary hover:bg-primary/90 px-6"
          data-testid="paywall-buy-btn"
        >
          <Sparkle weight="fill" className="w-4 h-4 mr-2" />
          Buy credits · $10 each
        </Button>
        {dismissable && (
          <Button
            variant="outline"
            onClick={onDismiss}
            className="min-h-12"
            data-testid="paywall-dismiss-btn"
          >
            Not now
          </Button>
        )}
      </div>
      <div className="mt-4 text-[11px] font-mono-tech text-muted-foreground">
        {remaining === 0
          ? "No credits on your account. One credit unlocks the rest of this case."
          : `${remaining} credit${remaining === 1 ? "" : "s"} available — one will be used to continue.`}
      </div>
    </div>
  );
}

